export type ResolvedTheme = 'light' | 'dark';
export type Appearance = ResolvedTheme | 'system';

const THEME_STORAGE_KEY = 'vite-ui-theme';

const prefersDarkScheme = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const resolveStoredAppearance = (): Appearance => {
    if (typeof window === 'undefined') {
        return 'system';
    }

    const storedAppearance = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (
        storedAppearance === 'light' ||
        storedAppearance === 'dark' ||
        storedAppearance === 'system'
    ) {
        return storedAppearance;
    }

    return 'system';
};

const resolveTheme = (appearance: Appearance): ResolvedTheme => {
    if (appearance === 'system') {
        return prefersDarkScheme() ? 'dark' : 'light';
    }

    return appearance;
};

const applyThemeClass = (theme: ResolvedTheme) => {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.classList.toggle('dark', theme === 'dark');
};

export class ThemeService {
    static isAppearance(value: string): value is Appearance {
        return value === 'light' || value === 'dark' || value === 'system';
    }

    static getAppearance(): Appearance {
        return resolveStoredAppearance();
    }

    static getResolvedTheme(): ResolvedTheme {
        return resolveTheme(this.getAppearance());
    }

    static applyAppearance(appearance: Appearance) {
        applyThemeClass(resolveTheme(appearance));
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(THEME_STORAGE_KEY, appearance);
            window.dispatchEvent(new CustomEvent('theme-change'));
        }
    }

    static syncTheme() {
        applyThemeClass(this.getResolvedTheme());
    }
}
